import { useEffect, useMemo, useState, type FC } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { IconPlus } from "@/assets/icons/geist/IconPlus";
import { Button } from "@/components/flexkit/Button";
import { WebhookList } from "@/components/webhooks/WebhookList";
import { CreateWebhookDialog } from "@/components/webhooks/CreateWebhookDialog";
import { webhookDetailsUrl, workflowsUrl } from "@/utils/automations";
import { atom, useAtomValue, useSetAtom, type PrimitiveAtom } from "jotai";
import { IconCrossSmall } from "central-icons/IconCrossSmall";
import { cn } from "@/components/podkit/lib/cn";
import { withGitpodSize } from "@/assets/icons/utils";

const IconCrossSmallWithSize = withGitpodSize(IconCrossSmall);

export const WebhooksSidePanel: FC = () => {
    const navigate = useNavigate();
    const isCreateWebhookDialogOpenAtom = useMemo(() => atom(false), []);
    const setIsCreateWebhookDialogOpen = useSetAtom(isCreateWebhookDialogOpenAtom);
    const [isOpen, setIsOpen] = useState(false);
    const [isHidden, setIsHidden] = useState(false);

    // Trigger entrance animation after mount
    useEffect(() => {
        requestAnimationFrame(() => {
            setIsOpen(true);
        });
    }, []);

    const handleClose = () => {
        setIsOpen(false);
    };

    const handleTransitionEnd = () => {
        if (!isOpen) {
            setIsHidden(true);
            navigate(workflowsUrl());
        }
    };

    const handleCreateWebhookSuccess = (webhookId: string) => {
        setIsCreateWebhookDialogOpen(false);
        navigate(webhookDetailsUrl(webhookId), { replace: true });
    };

    if (isHidden) {
        return null;
    }

    return (
        <>
            <div
                className={cn(
                    "absolute bottom-0 right-0 top-0 flex w-full max-w-2xl flex-col rounded-r-lg border-l border-border-base bg-surface-primary shadow-lg transition-transform duration-200",
                    isOpen ? "translate-x-0" : "translate-x-full",
                )}
                onTransitionEnd={handleTransitionEnd}
                data-testid="webhooks-side-panel"
            >
                <header className="flex shrink-0 items-center justify-between p-6">
                    <h2 className="text-lg font-medium leading-none tracking-tight text-content-primary">Webhooks</h2>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="primary"
                            LeadingIcon={IconPlus}
                            onClick={() => setIsCreateWebhookDialogOpen(true)}
                            data-testid="new-webhook-button"
                            data-tracking-id="new-webhook-side-panel"
                        >
                            New webhook
                        </Button>
                        <Button
                            variant="outline"
                            aria-label="Close webhooks panel"
                            onClick={handleClose}
                            data-tracking-id="close-webhooks-side-panel"
                            LeadingIcon={IconCrossSmallWithSize}
                        />
                    </div>
                </header>
                <div className="flex-1 overflow-y-auto p-6 pt-0">
                    <WebhookList />
                </div>
            </div>
            <Outlet />
            <CreateWebhookDialogWrapper
                isCreateWebhookDialogOpenAtom={isCreateWebhookDialogOpenAtom}
                onSuccess={handleCreateWebhookSuccess}
            />
        </>
    );
};

const CreateWebhookDialogWrapper: FC<{
    isCreateWebhookDialogOpenAtom: PrimitiveAtom<boolean>;
    onSuccess: (webhookId: string) => void;
}> = ({ isCreateWebhookDialogOpenAtom, onSuccess }) => {
    const isCreateWebhookDialogOpen = useAtomValue(isCreateWebhookDialogOpenAtom);
    const setIsCreateWebhookDialogOpen = useSetAtom(isCreateWebhookDialogOpenAtom);

    const handleClose = () => setIsCreateWebhookDialogOpen(false);

    return <CreateWebhookDialog open={isCreateWebhookDialogOpen} onClose={handleClose} onSuccess={onSuccess} />;
};
